import { z } from 'zod';

// src/version.ts
var PROTOCOL_VERSION = 1;
var SUPPORTED_VERSIONS = [1];
function isSupportedProtocolVersion(version) {
  return typeof version === "number" && SUPPORTED_VERSIONS.includes(version);
}

// src/errors.ts
function createProtocolError(code, message) {
  const error = new Error(message);
  error.name = "ProtocolError";
  error.code = code;
  return error;
}
function isProtocolError(value) {
  return value instanceof Error && value.name === "ProtocolError";
}
var MAX_TIMESTAMP_SKEW_MS = 5 * 60 * 1e3;
var baseEnvelopeSchema = z.object({
  v: z.literal(PROTOCOL_VERSION),
  id: z.string().min(1),
  ts: z.number().int().nonnegative(),
  token: z.string().optional()
});
function envelope(type, payload, token) {
  return { v: PROTOCOL_VERSION, id: nextEnvelopeId(), type, payload, ts: Date.now(), token };
}
var sequence = 0;
function nextEnvelopeId(prefix = "msg") {
  sequence = (sequence + 1) % 1e6;
  return `${prefix}_${Date.now().toString(36)}_${sequence.toString(36)}`;
}
function isFreshTimestamp(timestamp, now = Date.now()) {
  return Math.abs(now - timestamp) <= MAX_TIMESTAMP_SKEW_MS;
}
var MAX_TIMESTAMP_SKEW = MAX_TIMESTAMP_SKEW_MS;
var consentStateSchema = z.enum(["unknown", "denied", "essential_only", "granted"]);
var consentSourceSchema = z.enum(["default", "host", "cmp", "gpc", "tcf", "user", "revoke"]);
var consentSnapshotSchema = z.object({
  state: consentStateSchema,
  source: consentSourceSchema,
  timestamp: z.number().int().nonnegative(),
  tcString: z.string().optional(),
  gpc: z.boolean().optional(),
  categories: z.array(z.string()).optional()
});
var themeSchema = z.object({
  primaryColor: z.string().optional(),
  radius: z.union([z.string(), z.number()]).optional(),
  colorScheme: z.enum(["light", "dark", "auto"]).optional(),
  fontFamily: z.string().optional()
}).partial();
var userTraitsSchema = z.object({
  email: z.string().email().optional(),
  fullName: z.string().optional(),
  phoneNumber: z.string().optional(),
  avatar: z.string().url().optional(),
  role: z.string().optional()
}).catchall(z.unknown());
var sessionContextSchema = z.object({
  sessionId: z.string().min(1),
  anonymousId: z.string().optional(),
  projectUserId: z.string().optional(),
  uniqueId: z.string().optional(),
  userId: z.string().optional(),
  traits: userTraitsSchema.optional()
});
var ratingStyleSchema = z.enum(["stars", "thumbs", "nps", "emoji", "number"]);
var labelsSchema = z.object({
  submit: z.string().optional(),
  thanks: z.string().optional(),
  placeholder: z.string().optional()
}).partial();
var widgetStepSchema = z.object({
  kind: z.string(),
  question: z.string().optional(),
  required: z.boolean().optional(),
  options: z.array(z.unknown()).optional(),
  ratingStyle: ratingStyleSchema.optional(),
  leftLabel: z.string().optional(),
  rightLabel: z.string().optional(),
  placeholder: z.string().optional(),
  allowAttachments: z.boolean().optional(),
  attachTypes: z.array(z.string()).optional(),
  askName: z.boolean().optional(),
  askEmail: z.boolean().optional(),
  identityRequired: z.enum(["none", "email", "both"]).optional(),
  thanksTitle: z.string().optional(),
  thanksBody: z.string().optional(),
  thanksImageUrl: z.string().optional(),
  thanksCtaLabel: z.string().optional(),
  thanksCtaUrl: z.string().optional()
}).passthrough();
var launcherSchema = z.object({
  mode: z.string().optional(),
  label: z.string().optional(),
  icon: z.string().optional(),
  customIcon: z.string().optional(),
  sidePeek: z.number().optional()
}).partial();
var feedbackInlineConfigSchema = z.object({
  name: z.string().min(1),
  type: z.enum(["general", "feature", "other"]),
  title: z.string().optional(),
  description: z.string().optional(),
  rating: z.union([z.literal(false), ratingStyleSchema]).optional(),
  comment: z.boolean().optional(),
  attachments: z.boolean().optional(),
  // MIME-type filter that gates the file picker (e.g. ["image/*", "application/pdf"]).
  // If omitted, accepts any file type the OS picker offers.
  attachTypes: z.array(z.string()).optional(),
  // 'both' lets the widget collect a name AND an email; matches the main app's
  // ProjectFeedbackWidget identityRequired field.
  identity: z.enum(["none", "email", "full", "both"]).optional(),
  theme: themeSchema.optional(),
  labels: labelsSchema.optional(),
  category: z.string().optional(),
  metadata: z.record(z.unknown()).optional(),
  // When the dashboard has a custom multi-step flow defined, the full step
  // list flows through here and supersedes the flat title/rating/comment fields.
  steps: z.array(widgetStepSchema).optional(),
  // Per-launcher tweaks (mode, label, icon, sidePeek) — supersede project-level
  // launcher settings when set.
  launcher: launcherSchema.optional(),
  // Optional richer thanks screen — image + CTA for post-submit branding.
  thanks: z.object({
    title: z.string().optional(),
    body: z.string().optional(),
    imageUrl: z.string().optional(),
    ctaLabel: z.string().optional(),
    ctaUrl: z.string().optional()
  }).partial().optional()
});
var feedbackEventPayloadSchema = z.object({
  name: z.string(),
  type: z.string().optional(),
  response: z.unknown().optional(),
  reason: z.string().optional()
});
var feedbackOpenPayloadSchema = z.object({
  config: feedbackInlineConfigSchema.optional(),
  widget: z.unknown().optional()
});
var surveyOpenOptsSchema = z.object({
  force: z.boolean().optional(),
  variables: z.record(z.unknown()).optional(),
  theme: themeSchema.optional()
}).partial();
var surveyOpenPayloadSchema = z.object({
  publicId: z.string().min(1),
  prefetched: z.unknown().optional(),
  opts: surveyOpenOptsSchema.optional(),
  source: z.enum(["code", "urlAuto"]).optional(),
  // When true, the SDK is still resolving the payload in parallel with the
  // iframe boot. The shell should render its skeleton and wait for the
  // chirp:survey:payload follow-up envelope rather than firing its own fetch.
  awaitingPayload: z.boolean().optional()
});
var surveyPayloadEnvelopeSchema = z.object({
  publicId: z.string().min(1),
  payload: z.unknown()
});
var surveyPayloadErrorEnvelopeSchema = z.object({
  publicId: z.string().min(1),
  code: z.string(),
  message: z.string()
});
var surveyEventPayloadSchema = z.object({
  surveyId: z.string(),
  response: z.unknown().optional(),
  reason: z.string().optional()
});
var surveySuppressPayloadSchema = z.object({
  surveyId: z.string(),
  scope: z.enum(["user", "session"]).optional()
});
var announcementLayoutSchema = z.enum(["modal", "drawer", "banner", "toast", "inline"]);
var announcementPositionSchema = z.enum([
  "top",
  "bottom",
  "left",
  "right",
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right",
  "center"
]);
var announcementOpenModeSchema = z.enum(["single", "collection", "global"]);
var announcementOpenPayloadSchema = z.object({
  mode: announcementOpenModeSchema.default("single"),
  publicId: z.string().optional(),
  collectionSlug: z.string().optional(),
  layout: announcementLayoutSchema.optional(),
  position: announcementPositionSchema.optional(),
  layoutSettings: z.record(z.unknown()).optional(),
  prefetched: z.unknown().optional(),
  // See note on surveyOpenPayloadSchema.awaitingPayload — same semantics for
  // announcements when the SDK resolves a cold-cache announcement in parallel.
  awaitingPayload: z.boolean().optional()
});
var announcementPayloadEnvelopeSchema = z.object({
  publicId: z.string().min(1),
  payload: z.unknown()
});
var announcementPayloadErrorEnvelopeSchema = z.object({
  publicId: z.string().min(1),
  code: z.string(),
  message: z.string()
});
var announcementEventPayloadSchema = z.object({
  id: z.string(),
  internalId: z.string().optional(),
  ctaUrl: z.string().optional(),
  reason: z.string().optional()
});
var surfacePlacementPayloadSchema = z.object({
  layout: announcementLayoutSchema,
  position: announcementPositionSchema.optional(),
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
  inset: z.number().nonnegative().optional()
});
var captureKindSchema = z.enum(["screenshot", "recording"]);
var captureStatusSchema = z.enum(["success", "cancelled", "error"]);
var captureRequestSchema = z.object({
  kind: captureKindSchema,
  requestId: z.string()
});
var captureResultSchema = z.object({
  requestId: z.string(),
  kind: captureKindSchema,
  status: captureStatusSchema,
  dataUrl: z.string().optional(),
  mimeType: z.string().optional(),
  filename: z.string().optional(),
  error: z.string().optional()
});

// src/parse.ts
function parseEnvelope(schema, raw) {
  assertIsObject(raw);
  assertSupportedVersion(raw);
  const result = schema.safeParse(raw);
  if (!result.success) throw createProtocolError("schema", result.error.message);
  assertFreshTimestamp(result.data.ts);
  return result.data;
}
function assertIsObject(raw) {
  if (!raw || typeof raw !== "object") {
    throw createProtocolError("not_object", "envelope must be an object");
  }
}
function assertSupportedVersion(raw) {
  const version = raw.v;
  if (!isSupportedProtocolVersion(version)) {
    throw createProtocolError("bad_version", `unsupported protocol version: ${String(version)}`);
  }
}
function assertFreshTimestamp(ts) {
  if (!isFreshTimestamp(ts)) {
    throw createProtocolError("skew", `timestamp skew exceeds limit: ${ts}`);
  }
}

// src/inbound.ts
var hydratePayloadSchema = z.object({
  projectId: z.string().min(1),
  // Client UUID — required by public-SDK GraphQL mutations
  // (trackAnnouncementEvent etc.) via the x-chirp-client-id header. Optional
  // here for backwards-compat with older SDK builds that didn't forward it.
  clientId: z.string().optional(),
  apiHost: z.string().url(),
  session: sessionContextSchema,
  consent: consentSnapshotSchema,
  theme: themeSchema.optional(),
  buildSha: z.string().optional(),
  sdkVersion: z.string().optional(),
  // Host page context — recorded against analytics so events attribute
  // to the page the user was looking at, not the iframe URL. Optional so
  // older SDK callers still pass schema validation.
  host: z.object({
    url: z.string().optional(),
    referrer: z.string().optional()
  }).optional()
});
var identifyPayloadSchema = z.object({
  uniqueId: z.string().min(1),
  traits: userTraitsSchema.optional()
});
var emptyPayloadSchema = z.object({}).optional();
function inbound(type, payload) {
  return baseEnvelopeSchema.extend({ type: z.literal(type), payload });
}
var inboundEnvelopeSchema = z.discriminatedUnion("type", [
  inbound("chirp:hydrate", hydratePayloadSchema),
  inbound("chirp:survey:open", surveyOpenPayloadSchema),
  inbound("chirp:survey:payload", surveyPayloadEnvelopeSchema),
  inbound("chirp:survey:error", surveyPayloadErrorEnvelopeSchema),
  inbound("chirp:survey:close", emptyPayloadSchema),
  inbound("chirp:announcement:open", announcementOpenPayloadSchema),
  inbound("chirp:announcement:payload", announcementPayloadEnvelopeSchema),
  inbound("chirp:announcement:error", announcementPayloadErrorEnvelopeSchema),
  inbound("chirp:announcement:close", emptyPayloadSchema),
  inbound("chirp:feedback:open", feedbackOpenPayloadSchema),
  inbound("chirp:feedback:close", emptyPayloadSchema),
  inbound("chirp:identify", identifyPayloadSchema),
  inbound("chirp:reset", emptyPayloadSchema),
  inbound("chirp:teardown", emptyPayloadSchema),
  inbound("chirp:ping", emptyPayloadSchema),
  inbound("chirp:capture:result", captureResultSchema),
  // Forwarded from the parent page so users can press Escape on the host
  // (without first clicking into the cross-origin iframe) and still dismiss
  // the surface. Shell consumes this and re-fires its own keydown handlers.
  inbound("chirp:host:keydown", z.object({ key: z.string() }))
]);
function parseInbound(raw) {
  return parseEnvelope(inboundEnvelopeSchema, raw);
}
var readyPayloadSchema = z.object({
  sdkVersion: z.string().optional(),
  buildSha: z.string().optional()
});
var resizePayloadSchema = z.object({
  width: z.number().nonnegative().optional(),
  height: z.number().nonnegative().optional()
});
var errorPayloadSchema = z.object({
  code: z.string(),
  message: z.string(),
  context: z.record(z.unknown()).optional()
});
var perfFirstPaintPayloadSchema = z.object({
  surface: z.enum(["survey", "announcement", "feedback"]),
  publicId: z.string().optional(),
  // Was the renderer mounted with payload (content) or just skeleton?
  hasContent: z.boolean()
});
var emptyPayloadSchema2 = z.object({}).optional();
function outbound(type, payload) {
  return baseEnvelopeSchema.extend({ type: z.literal(type), payload });
}
var outboundEnvelopeSchema = z.discriminatedUnion("type", [
  outbound("chirp:ready", readyPayloadSchema),
  outbound("chirp:pong", emptyPayloadSchema2),
  outbound("chirp:resize", resizePayloadSchema),
  outbound("chirp:survey:shown", surveyEventPayloadSchema),
  outbound("chirp:survey:completed", surveyEventPayloadSchema),
  outbound("chirp:survey:dismissed", surveyEventPayloadSchema),
  outbound("chirp:survey:suppress", surveySuppressPayloadSchema),
  outbound("chirp:announcement:shown", announcementEventPayloadSchema),
  outbound("chirp:announcement:clicked", announcementEventPayloadSchema),
  outbound("chirp:announcement:dismissed", announcementEventPayloadSchema),
  outbound("chirp:changelog:shown", emptyPayloadSchema2),
  outbound("chirp:changelog:dismissed", emptyPayloadSchema2),
  outbound("chirp:surface:placement", surfacePlacementPayloadSchema),
  outbound("chirp:feedback:shown", feedbackEventPayloadSchema),
  outbound("chirp:feedback:submitted", feedbackEventPayloadSchema),
  outbound("chirp:feedback:dismissed", feedbackEventPayloadSchema),
  outbound("chirp:error", errorPayloadSchema),
  outbound("chirp:token:request", emptyPayloadSchema2),
  outbound("chirp:capture:request", captureRequestSchema),
  outbound("chirp:perf:firstPaint", perfFirstPaintPayloadSchema)
]);
function parseOutbound(raw) {
  return parseEnvelope(outboundEnvelopeSchema, raw);
}

export { MAX_TIMESTAMP_SKEW, PROTOCOL_VERSION, announcementEventPayloadSchema, announcementLayoutSchema, announcementOpenModeSchema, announcementOpenPayloadSchema, announcementPayloadEnvelopeSchema, announcementPositionSchema, baseEnvelopeSchema, captureKindSchema, captureRequestSchema, captureResultSchema, captureStatusSchema, consentSnapshotSchema, consentSourceSchema, consentStateSchema, createProtocolError, envelope, feedbackEventPayloadSchema, feedbackInlineConfigSchema, feedbackOpenPayloadSchema, inboundEnvelopeSchema, isFreshTimestamp, isProtocolError, isSupportedProtocolVersion, nextEnvelopeId, outboundEnvelopeSchema, parseInbound, parseOutbound, sessionContextSchema, surfacePlacementPayloadSchema, surveyEventPayloadSchema, surveyOpenOptsSchema, surveyOpenPayloadSchema, surveyPayloadEnvelopeSchema, surveySuppressPayloadSchema, themeSchema, userTraitsSchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map